import React, { useEffect, useRef, useState } from 'react';
import { Button, SiteHeader } from '../components/bx';
import '../styles/bx.css';
import { confirmPayment, confirmSubscriptionPayment } from '../lib/data';

interface PaymentCallbackPageProps {
  reference: string | null;
  isSubscribe?: boolean;
  onPaymentConfirmed: (success: boolean, paymentData?: any) => void;
}

export default function PaymentCallbackPage({
  reference,
  isSubscribe,
  onPaymentConfirmed,
}: PaymentCallbackPageProps) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying payment...');
  const [details, setDetails] = useState('');
  const verifiedReferenceRef = useRef<string | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        if (!reference) {
          console.warn('No reference provided');
          return;
        }

        if (verifiedReferenceRef.current === reference) {
          return;
        }

        verifiedReferenceRef.current = reference;

        const result = isSubscribe
          ? await confirmSubscriptionPayment(reference)
          : await confirmPayment(reference);

        if (result.success === true) {
          setStatus('success');
          setMessage('Payment successful');
          setDetails(
            isSubscribe
              ? 'Your subscription has been confirmed.'
              : 'Your gift order has been confirmed.'
          );

          window.setTimeout(() => {
            onPaymentConfirmed(true, result.data);
          }, 200);
        } else {
          setStatus('error');
          setMessage('We couldn’t confirm your payment');
          setDetails(result.message || 'Payment verification failed. Please try again.');

          window.setTimeout(() => {
            onPaymentConfirmed(false);
          }, 200);
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
        setMessage('We couldn’t confirm your payment');
        setDetails('An error occurred while verifying your payment. Please try again.');

        window.setTimeout(() => {
          onPaymentConfirmed(false);
        }, 200);
      }
    };

    void verifyPayment();
  }, [isSubscribe, onPaymentConfirmed, reference]);

  return (
    <div className="bx">
      <SiteHeader />
      <main className="bx-main">
        <div className="bx-container">
          <div className="bx-card bx-status" role="status" aria-live="polite">
            <div className={`bx-status-icon${status === 'loading' ? ' pending' : status === 'error' ? ' fail' : ''}`} aria-hidden="true">
              {status === 'loading' ? <span className="bx-spinner" style={{ width: 30, height: 30, borderWidth: 3 }} /> : status === 'success' ? '✓' : '!'}
            </div>
            <h1>{status === 'loading' ? 'Confirming your payment…' : message}</h1>
            <p>{status === 'loading' ? 'This only takes a moment. Please don’t close this page.' : details}</p>
            {status === 'error' ? <p style={{ fontSize: 13.5 }}>If you were charged, don’t pay again — we’ll sort it out. Taking you back to checkout…</p> : null}
            {status === 'error' ? <Button variant="secondary" onClick={() => window.location.assign('/')}>Back to BOQBOX</Button> : null}
          </div>
        </div>
      </main>
    </div>
  );
}
